import { Divider, Form, FormInstance, Input, Select } from "antd";
import TextArea from "antd/lib/input/TextArea";
import UserTag from "components/user-tag/user-tag";
import FeatureSpecSectionDropdown from "components/feature-spec-section-dropdown";
import GCReviewersDropdown from "components/gc-reveiewer-dropdown";
import { AcceptanceStatus, ErrorMessages } from "constants/index";
import { useProjectParticipants } from "hooks/project-participants";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { useState } from "react";
import "./material-form-body.scss";
import { validatorForFieldValue } from "utils/utils";

const { Option } = Select;

type MaterialFormBodyProps = {
  form: FormInstance;
  materialHeaderMap: any;
  selectedSpecIds: string[];
  setSelectedSpecIds: React.Dispatch<React.SetStateAction<string[]>>;
  token: any;
  disabled?: boolean;
};

export default function MaterialFormBody({
  form,
  materialHeaderMap,
  selectedSpecIds,
  setSelectedSpecIds,
  token,
  disabled
}: MaterialFormBodyProps) {
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const { projectParticipants } = useProjectParticipants();

  const assigneeValue = Form.useWatch("assignee", form);
  const submitterUsers = projectParticipants?.submitterUsers?.filter(
    (user: any) => {
      if (assigneeValue && assigneeValue === user.id) return true;
      return user.status_id !== AcceptanceStatus.DEACTIVATED;
    }
  );

  return (
    <div className="material-form-body">
      <Form.Item
        name="title"
        label={materialHeaderMap?.name || "Material Title"}
        rules={[
          {
            required: true,
            message: "Enter material title."
          },
          {
            validator: (_, value) => {
              return validatorForFieldValue(
                "title",
                value?.trim(),
                "MATERIALS",
                token,
                ErrorMessages.MaterialTitleMsg
              );
            }
          }
        ]}
      >
        <Input
          placeholder="Prefilled if available"
          disabled={disabled}
          className="rounded-md"
        />
      </Form.Item>

      <Form.Item
        name="tag_name"
        label={materialHeaderMap?.tag_name || "Material Tag"}
      >
        <Input
          placeholder="Prefilled if available"
          disabled={disabled}
          className="rounded-md"
        />
      </Form.Item>

      <Form.Item
        name="manufacturer"
        label={materialHeaderMap?.manufacturer || "Manufacturer"}
      >
        <Input
          placeholder="Enter manufacturer"
          disabled={disabled}
          className="rounded-md"
        />
      </Form.Item>

      <Form.Item
        name="description"
        label={materialHeaderMap?.description || "Description"}
      >
        <TextArea
          placeholder="Placeholder"
          rows={3}
          disabled={disabled}
          className="rounded-md"
        />
      </Form.Item>

      <Divider style={{ margin: "16px 0 8px" }} />

      <div
        className="flex justify-between items-center cursor-pointer"
        style={{ padding: "8px 0" }}
        onClick={() => setShowMoreDetails(!showMoreDetails)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ")
            setShowMoreDetails(!showMoreDetails);
        }}
      >
        <span
          className="font-semibold text-ciq-black-a85 text-[13px]"
          style={{ fontSize: 14 }}
        >
          More Details
        </span>
        {showMoreDetails ? (
          <UpOutlined className="text-black text-xs" />
        ) : (
          <DownOutlined className="text-black text-xs" />
        )}
      </div>

      {showMoreDetails && (
        <div style={{ paddingTop: 12 }}>
          <Form.Item
            label={materialHeaderMap?.spec_section_no || "Spec Section"}
          >
            <FeatureSpecSectionDropdown
              mode="single"
              placeholder="Select"
              selectedIds={selectedSpecIds}
              onBlur={(ids) => {
                setSelectedSpecIds(ids);
              }}
              className="w-full rounded-select"
            />
          </Form.Item>

          <Form.Item name="assignee" label="Assignee">
            <Select
              className="w-full rounded-select"
              loading={!projectParticipants?.dataLoaded}
              placeholder="Select"
              allowClear
              showSearch
              filterOption
              optionFilterProp="label"
            >
              {submitterUsers?.map((user: any) => {
                const selectedAndInactive =
                  (user.status_id === AcceptanceStatus.DEACTIVATED &&
                    user.id === assigneeValue?.toString()) ??
                  "";

                return (
                  <Option
                    key={user.id}
                    value={user.id}
                    label={`${user.first_name} ${user.last_name} ${user?.company?.name}`}
                    className={selectedAndInactive ? "hidden" : ""}
                  >
                    {user.status_id === AcceptanceStatus.DEACTIVATED && (
                      <div className="absolute flex w-full items-center justify-end pr-3 -mt-[1px]">
                        <UserTag label="Inactive" />
                      </div>
                    )}
                    <div>
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {user?.company?.name}
                    </div>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item name="gc_representative" label="GC Representative">
            <GCReviewersDropdown className="rounded-select" />
          </Form.Item>
        </div>
      )}
    </div>
  );
}
